// router/ProtectedRoute.tsx
import { Navigate } from "react-router-dom";
import { AppShell } from "../components/app-shell";
import {
  useIsLoading,
  useIsFullyAuthenticated,
  useInvitationStatus,
  useSession,
  useRequiresPasswordReset,
} from "@/store";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const isLoading = useIsLoading();
  const isAuthenticated = useIsFullyAuthenticated();
  const invitationStatus = useInvitationStatus();
  const session = useSession();
  const requiresPasswordReset = useRequiresPasswordReset();

  // Show loading while checking company user or processing invitation
  if (isLoading) {
    let loadingMessage = "Loading...";

    if (invitationStatus === "checking") {
      loadingMessage = "Checking invitation...";
    } else if (invitationStatus === "processing") {
      loadingMessage = "Setting up your account...";
    }

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  // No session at all = redirect to login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Invitees / recovery users must set a password before using the app
  if (requiresPasswordReset) {
    return <Navigate to="/reset-password" replace />;
  }

  // Handle invitation statuses
  if (session && !isAuthenticated) {
    if (invitationStatus === "not_found") {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You don't have an active invitation for this application. Please
              contact your administrator.
            </p>
            <button
              onClick={() => (window.location.href = "/login")}
              className="text-teal hover:underline"
            >
              Return to Login
            </button>
          </div>
        </div>
      );
    }

    if (invitationStatus === "error") {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold mb-2">Setup Error</h2>
            <p className="text-muted-foreground mb-4">
              There was an error setting up your account. Please try refreshing
              the page or contact support.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="text-teal hover:underline mr-4"
            >
              Refresh Page
            </button>
            <button
              onClick={() => (window.location.href = "/login")}
              className="text-teal hover:underline"
            >
              Return to Login
            </button>
          </div>
        </div>
      );
    }

    // Fallback for other unauthenticated states
    return <Navigate to="/login" replace />;
  }

  // Fully authenticated = render protected content.
  return <AppShell>{children}</AppShell>;
};
